"use client";

import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
export default function Systemshieldfeatures() {
  const prefersReducedMotion = useReducedMotion();
  const fadeUp = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  };
  const cards = [{
    key: "encryption",
    eyebrow: "Military Grade",
    title: "Advanced Encryption",
    description: "Protect your digital footprint with our state-of-the-art AES-256 encryption protocols, ensuring your data remains private and secure.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_encryption_shield.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_encryption_shield.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_encryption_shield.webp-wvc-sizes",
      alt: "Digital shield icon representing advanced cybersecurity encryption"
    },
    href: "/VpngenixPage"
  }, {
    key: "servers",
    eyebrow: "Global Network",
    title: "High-Speed Servers",
    description: "Experience lightning-fast connection speeds with our optimized global server network, designed for seamless streaming and browsing.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_global_network.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_global_network.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_global_network.webp-wvc-sizes",
      alt: "Global network map showing high-speed server connections"
    },
    href: "/VpngenixPage"
  }, {
    key: "privacy",
    eyebrow: "Zero Logs",
    title: "Strict Privacy Policy",
    description: "We maintain a strict no-logs policy, ensuring that your online activities are never tracked, stored, or shared with third parties.",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_privacy_lock.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_privacy_lock.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpngenix_privacy_lock.webp-wvc-sizes",
      alt: "Privacy lock icon symbolizing zero-log data protection"
    },
    href: "/VpngenixPage"
  }];
  return <section data-section-id="27652" id="systemshieldfeatures" className="relative bg-background text-foreground py-32 md:py-44">
      <div aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-24 bg-primary" />

      <div className="mx-auto w-full max-w-[1360px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-8">
          <motion.div {...fadeUp} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.7,
          ease: [0.2, 0.6, 0.2, 1]
        }} className="lg:col-span-7">
            <p className="font-default italic uppercase text-xs md:text-sm mb-6 text-primary tracking-[0.14em]" style={{
            fontVariant: "small-caps"
          }}>
              <span aria-hidden="true" className="inline-block align-middle h-px w-6 mr-3 bg-primary" />
              Vpngenix Security
            </p>
            <h1 className="font-default text-foreground" style={{
            fontSize: "clamp(2rem, 4.2vw, 4rem)",
            fontWeight: 700,
            letterSpacing: "-0.015em",
            lineHeight: 1.08
          }}>
              Unmatched Digital Protection for the <em className="italic font-default">Modern Era</em>
            </h1>
          </motion.div>

          <motion.div {...fadeUp} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.7,
          ease: [0.2, 0.6, 0.2, 1],
          delay: prefersReducedMotion ? 0 : 0.08
        }} className="lg:col-span-5 mt-10 lg:mt-4">
            <div className="font-default" style={{
            maxWidth: "480px",
            color: "var(--muted-foreground)",
            fontSize: "clamp(1rem, 1.05vw, 1.0625rem)",
            lineHeight: 1.65,
            letterSpacing: "0.005em"
          }}>
              <p className="mb-5">
                Vpngenix provides a robust shield for your digital life. Our infrastructure is built on the principles of transparency, speed, and absolute privacy, ensuring you stay connected without compromise.
              </p>
              <p className="mb-8">
                From military-grade encryption to our global network of high-speed servers, we deliver a seamless experience that keeps your data safe from prying eyes, no matter where you are in the world.
              </p>
              <Link to="#" className="font-default italic underline decoration-1 underline-offset-4 transition-colors duration-300 hover:text-primary text-foreground" style={{
              fontSize: "clamp(1rem, 1.05vw, 1.0625rem)"
            }}>
                Discover our security standards
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((card, i) => <motion.article key={card.key} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.7,
          ease: [0.2, 0.6, 0.2, 1],
          delay: prefersReducedMotion ? 0 : i * 0.08
        }} className="group relative flex flex-col bg-card shadow-md rounded-lg">
              <div className="relative overflow-hidden rounded-t-lg" style={{
            aspectRatio: "3 / 4"
          }}>
                <img src={card.image.src} data-wvc-srcset={card.image.srcset} data-wvc-sizes={card.image.sizes} alt={card.image.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />
              </div>

              <div className="flex flex-1 flex-col p-6 md:p-8">
                <p className="font-default italic uppercase text-xs mb-5 flex items-center text-primary tracking-[0.14em]" style={{
              fontVariant: "small-caps"
            }}>
                  <span aria-hidden="true" className="inline-block h-px w-6 mr-3 bg-primary" />
                  {card.eyebrow}
                </p>

                <h3 className="font-default text-foreground mb-4" style={{
              fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)",
              fontWeight: 600,
              letterSpacing: "-0.005em",
              lineHeight: 1.25
            }}>
                  {card.title}
                </h3>

                <p className="font-default mb-8 text-muted-foreground" style={{
              fontSize: "clamp(1rem, 1.05vw, 1.0625rem)",
              lineHeight: 1.65
            }}>
                  {card.description}
                </p>

                <div className="mt-auto pt-6">
                  <Link to={card.href} className="group/link inline-flex items-center gap-2 font-default uppercase text-xs text-foreground transition-colors duration-300 hover:text-primary tracking-[0.14em]">
                    <span className="relative">
                      Explore
                      <span aria-hidden="true" className="absolute left-0 -bottom-1.5 h-px w-full origin-right transition-transform duration-300 ease-out group-hover/link:scale-x-125 bg-primary" />
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-1" strokeWidth={1} />
                  </Link>
                </div>
              </div>

              <span aria-hidden="true" className="absolute bottom-0 left-0 h-px w-full bg-border" />
              <span aria-hidden="true" className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 transition-transform duration-[400ms] ease-out group-hover:scale-x-100 bg-primary" />
            </motion.article>)}
        </div>
      </div>
    </section>;
}